import React, { useState, useEffect, useCallback } from 'react';
import api from '../core/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SecurityEvent {
    id: string;
    kind: string;
    label: string;
    message: string;
    patientPhoneMasked: string | null;
    patientId: string | null;
    organizationId: string | null;
    createdAt: string;
}

interface SecurityData {
    total: number;
    byKind: Record<string, number>;
    events: SecurityEvent[];
}

interface BufferStats {
    activeKeys: number;
    windowMs: number;
    maxWaitMs: number;
}

// ─── Meta por tipo de amenaza ────────────────────────────────────────────────

const KIND_META: Record<string, { label: string; icon: string; color: string; badge: string }> = {
    prompt_injection: {
        label: 'Inyección de prompt', icon: 'swap_horiz', color: 'text-brand-danger',
        badge: 'bg-brand-danger/12 text-brand-danger dark:bg-brand-danger/30 dark:text-brand-danger',
    },
    system_extraction: {
        label: 'Extracción del sistema', icon: 'policy', color: 'text-amber-500',
        badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    },
    credential_probe: {
        label: 'Sondeo de credenciales', icon: 'key_off', color: 'text-brand-danger',
        badge: 'bg-brand-danger/12 text-brand-danger dark:bg-brand-danger/30 dark:text-brand-danger',
    },
    role_impersonation: {
        label: 'Suplantación de admin', icon: 'person_alert', color: 'text-orange-500',
        badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    },
    code_request: {
        label: 'Código / inyección', icon: 'code_off', color: 'text-primary',
        badge: 'bg-primary/12 text-primary dark:bg-primary/30 dark:text-primary',
    },
    response_blocked: {
        label: 'Respuesta bloqueada', icon: 'shield_lock', color: 'text-brand-accent',
        badge: 'bg-brand-accent/12 text-brand-accent dark:bg-brand-accent/30 dark:text-brand-accent',
    },
    auto_blocked: {
        label: 'Contacto bloqueado', icon: 'block', color: 'text-brand-danger',
        badge: 'bg-brand-danger text-white dark:bg-brand-danger dark:text-white',
    },
};

const kindMeta = (kind: string) =>
    KIND_META[kind] || { label: kind, icon: 'help', color: 'text-slate-400', badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    const hrs = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins}m`;
    if (hrs < 24) return `Hace ${hrs}h`;
    if (days < 30) return `Hace ${days}d`;
    return new Date(iso).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
}

function fullDate(iso: string): string {
    return new Date(iso).toLocaleString('es-DO', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}

// ─── Main ────────────────────────────────────────────────────────────────────

const Security: React.FC = () => {
    const [data, setData] = useState<SecurityData | null>(null);
    const [buffer, setBuffer] = useState<BufferStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>('all');
    const [expanded, setExpanded] = useState<string | null>(null);
    const [unblocking, setUnblocking] = useState<string | null>(null);
    const [unblocked, setUnblocked] = useState<Set<string>>(new Set());
    const [toast, setToast] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [secRes, bufRes] = await Promise.all([
                api.get('/monitoring/security?limit=200'),
                api.get('/monitoring/message-buffer').catch(() => null),
            ]);
            if (secRes.data?.success) setData(secRes.data);
            if (bufRes?.data?.success) setBuffer(bufRes.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const unblock = async (patientId: string) => {
        setUnblocking(patientId);
        try {
            await api.post('/monitoring/security/unblock', { patientId });
            setUnblocked(prev => new Set([...prev, patientId]));
            setToast('Diana volverá a responder a este contacto');
        } catch {
            setToast('No se pudo reactivar. Intenta de nuevo.');
        } finally {
            setUnblocking(null);
            setTimeout(() => setToast(null), 4000);
        }
    };

    const events = data?.events || [];
    const filtered = filter === 'all' ? events : events.filter(e => e.kind === filter);

    // Tipos presentes, ordenados por frecuencia
    const kinds = Object.entries(data?.byKind || {}).sort((a, b) => b[1] - a[1]);

    // Intentos en las últimas 24h — señal de actividad reciente
    const last24h = events.filter(e => Date.now() - new Date(e.createdAt).getTime() < 86_400_000).length;

    // Contactos actualmente silenciados (descontando los ya reactivados aquí)
    const blockedCount = events
        .filter(e => e.kind === 'auto_blocked' && e.patientId && !unblocked.has(e.patientId))
        .reduce((set, e) => set.add(e.patientId!), new Set<string>()).size;

    return (
        <div className="px-4 pt-6 pb-10 max-w-3xl mx-auto space-y-5">

            {/* Toast */}
            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium shadow-lg">
                    {toast}
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-slate-900 dark:text-white">Seguridad</h1>
                    <p className="text-xs text-slate-400 mt-0.5">Intentos de manipulación contra Diana</p>
                </div>
                <button
                    onClick={load}
                    className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    title="Actualizar"
                >
                    <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>refresh</span>
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-300">
                    <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
                </div>
            ) : (
                <>
                    {/* Resumen */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
                            <span className="material-symbols-outlined text-[22px] text-slate-400">shield</span>
                            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{events.length}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">Intentos registrados</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
                            <span className={`material-symbols-outlined text-[22px] ${last24h > 0 ? 'text-amber-500' : 'text-slate-400'}`}>schedule</span>
                            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{last24h}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">Últimas 24 h</p>
                        </div>
                        <div className={`rounded-2xl border p-4 ${
                            blockedCount > 0
                                ? 'border-brand-danger/20 dark:border-brand-danger/40 bg-brand-danger/8 dark:bg-brand-danger/20'
                                : 'border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card'
                        }`}>
                            <span className={`material-symbols-outlined text-[22px] ${blockedCount > 0 ? 'text-brand-danger' : 'text-brand-accent'}`}>
                                {blockedCount > 0 ? 'block' : 'verified_user'}
                            </span>
                            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">
                                {blockedCount > 0 ? blockedCount : (data?.byKind?.response_blocked || 0)}
                            </p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                                {blockedCount > 0 ? 'Contactos bloqueados' : 'Fugas bloqueadas'}
                            </p>
                        </div>
                    </div>

                    {/* Aviso de protección automática activa */}
                    {blockedCount > 0 && (
                        <div className="rounded-2xl border border-brand-danger/20 dark:border-brand-danger/40 bg-brand-danger/8 dark:bg-brand-danger/20 px-4 py-3 flex items-start gap-3">
                            <span className="material-symbols-outlined text-[20px] text-brand-danger shrink-0 mt-0.5">shield_person</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-brand-danger dark:text-brand-danger">
                                    Protección automática activada
                                </p>
                                <p className="text-xs text-brand-danger/80 dark:text-brand-danger/80 mt-0.5 leading-relaxed">
                                    Diana dejó de responder a {blockedCount === 1 ? 'un contacto' : `${blockedCount} contactos`} por insistir en manipularla.
                                    Sus mensajes siguen llegando a la bandeja para atención humana. Abre el evento para reactivarla.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Estado del buffer de mensajes */}
                    {buffer && (
                        <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card px-4 py-3 flex items-center gap-3">
                            <span className={`material-symbols-outlined text-[20px] ${buffer.activeKeys > 0 ? 'text-primary' : 'text-slate-300 dark:text-slate-600'}`}>
                                forum
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-800 dark:text-white">
                                    {buffer.activeKeys > 0
                                        ? `${buffer.activeKeys} ${buffer.activeKeys === 1 ? 'conversación agrupando' : 'conversaciones agrupando'} mensajes`
                                        : 'Sin ráfagas en curso'}
                                </p>
                                <p className="text-[11px] text-slate-400 mt-0.5">
                                    Diana espera {Math.round(buffer.windowMs / 1000)}s tras el último mensaje antes de responder (máx {Math.round(buffer.maxWaitMs / 1000)}s)
                                </p>
                            </div>
                            {buffer.activeKeys > 0 && (
                                <span className="w-2 h-2 rounded-full bg-primary/90 animate-pulse shrink-0" />
                            )}
                        </div>
                    )}

                    {events.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-600 gap-3">
                            <span className="material-symbols-outlined text-[48px] text-brand-accent">verified_user</span>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Ningún intento de manipulación registrado</p>
                            <p className="text-xs text-slate-400 text-center max-w-xs">
                                Aquí aparecerán los mensajes que intenten extraer información interna o alterar el comportamiento de Diana.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Filtros por tipo */}
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => setFilter('all')}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                                        filter === 'all'
                                            ? 'bg-primary/90 text-white shadow-sm'
                                            : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                                    }`}
                                >
                                    Todos ({events.length})
                                </button>
                                {kinds.map(([kind, count]) => {
                                    const m = kindMeta(kind);
                                    return (
                                        <button
                                            key={kind}
                                            onClick={() => setFilter(kind)}
                                            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                                                filter === kind
                                                    ? 'bg-primary/90 text-white shadow-sm'
                                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                                            }`}
                                        >
                                            {m.label} ({count})
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Lista de eventos */}
                            <div className="space-y-2">
                                {filtered.map(ev => {
                                    const m = kindMeta(ev.kind);
                                    const isOpen = expanded === ev.id;
                                    const preview = ev.message.length > 90 ? ev.message.slice(0, 90) + '…' : ev.message;
                                    return (
                                        <div
                                            key={ev.id}
                                            className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card overflow-hidden"
                                        >
                                            <button
                                                className="w-full text-left px-4 py-3.5 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                                                onClick={() => setExpanded(isOpen ? null : ev.id)}
                                            >
                                                <span className={`material-symbols-outlined text-[20px] ${m.color} shrink-0 mt-0.5`}>
                                                    {m.icon}
                                                </span>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${m.badge}`}>
                                                            {m.label}
                                                        </span>
                                                        <span className="text-[11px] text-slate-400">{timeAgo(ev.createdAt)}</span>
                                                    </div>
                                                    <p className="text-sm text-slate-700 dark:text-slate-200 mt-1.5 break-words font-mono leading-snug">
                                                        {preview}
                                                    </p>
                                                    {ev.patientPhoneMasked && (
                                                        <p className="text-[11px] text-slate-400 mt-1">
                                                            Desde {ev.patientPhoneMasked}
                                                        </p>
                                                    )}
                                                </div>

                                                <span className={`material-symbols-outlined text-[18px] text-slate-300 dark:text-slate-600 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
                                                    expand_more
                                                </span>
                                            </button>

                                            {isOpen && (
                                                <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-white/5 space-y-3">
                                                    <div>
                                                        <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Mensaje completo</p>
                                                        <p className="text-sm text-slate-700 dark:text-slate-200 break-words font-mono bg-slate-50 dark:bg-slate-800/60 rounded-xl p-3 leading-relaxed">
                                                            {ev.message}
                                                        </p>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                                        <div>
                                                            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Detectado por</p>
                                                            <p className="text-slate-700 dark:text-slate-200 mt-0.5">{ev.label}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Fecha</p>
                                                            <p className="text-slate-700 dark:text-slate-200 mt-0.5">{fullDate(ev.createdAt)}</p>
                                                        </div>
                                                        {ev.patientPhoneMasked && (
                                                            <div>
                                                                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Teléfono</p>
                                                                <p className="text-slate-700 dark:text-slate-200 mt-0.5">{ev.patientPhoneMasked}</p>
                                                            </div>
                                                        )}
                                                        {ev.organizationId && (
                                                            <div className="min-w-0">
                                                                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Organización</p>
                                                                <p className="text-slate-700 dark:text-slate-200 mt-0.5 truncate" title={ev.organizationId}>
                                                                    {ev.organizationId}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Reactivar a Diana con un contacto bloqueado */}
                                                    {ev.kind === 'auto_blocked' && ev.patientId && (
                                                        unblocked.has(ev.patientId) ? (
                                                            <p className="text-xs text-brand-accent dark:text-brand-accent flex items-center gap-1.5">
                                                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                                Diana ya responde a este contacto
                                                            </p>
                                                        ) : (
                                                            <button
                                                                onClick={() => unblock(ev.patientId!)}
                                                                disabled={unblocking === ev.patientId}
                                                                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
                                                            >
                                                                <span className={`material-symbols-outlined text-[16px] ${unblocking === ev.patientId ? 'animate-spin' : ''}`}>
                                                                    {unblocking === ev.patientId ? 'progress_activity' : 'lock_open'}
                                                                </span>
                                                                Reactivar a Diana con este contacto
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <p className="text-[11px] text-slate-400 text-center pt-2">
                                Diana rechaza estos intentos automáticamente. Este registro es solo para tu visibilidad.
                            </p>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default Security;
